/**
 * TerminateInspector — Phase 5a-5.
 *
 * Edits the `terminate` node — ends a workflow branch with an explicit
 * status. Fields: status (success/failed/cancelled), message, and an
 * optional output payload (JSON textarea).
 *
 * Spread-preservation contract identical to HTTP/LLM/Condition editors.
 */

import React from 'react';

const STATUSES = [
  { value: 'COMPLETED', label: 'completed (success)', tone: '#10b981' },
  { value: 'FAILED',    label: 'failed',              tone: '#ef4444' },
  { value: 'CANCELED',  label: 'cancelled',           tone: '#64748b' },
];

export default function TerminateInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const status = (cfg.status || 'COMPLETED').toUpperCase();
  const message = cfg.message || '';
  const outputStr = typeof cfg.output === 'string'
    ? cfg.output
    : (cfg.output ? JSON.stringify(cfg.output, null, 2) : '');

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  const setOutput = (raw) => {
    if (typeof raw !== 'string') return setField('output', raw);
    if (raw.trim() === '') return setField('output', undefined);
    try { setField('output', JSON.parse(raw)); }
    catch (_) { setField('output', raw); /* keep raw so user can fix syntax */ }
  };

  const currentStatus = STATUSES.find((s) => s.value === status) || STATUSES[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Status</span>
        <select
          value={status}
          onChange={(e) => setField('status', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#64748b' }}>
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background: currentStatus.tone,
          }} />
          Workflow branch ends with {currentStatus.label}.
        </span>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Message</span>
        <textarea
          value={message}
          onChange={(e) => setField('message', e.target.value)}
          placeholder="Branch completed: alert was a false positive"
          disabled={readOnly}
          rows={3}
          style={textareaStyle}
        />
        <span style={{ fontSize: 10, color: '#64748b' }}>
          Optional. Surfaces in audit log + execution timeline.
        </span>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Output</span>
        <textarea
          value={outputStr}
          onChange={(e) => setOutput(e.target.value)}
          placeholder='{ "verdict": "false_positive", "reviewer": "auto" }'
          disabled={readOnly}
          rows={4}
          style={{ ...textareaStyle, fontFamily: 'SFMono-Regular, Menlo, monospace' }}
        />
        <span style={{ fontSize: 10, color: '#64748b' }}>
          Optional JSON object passed back to the workflow caller / parent runtime.
        </span>
      </label>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '6px 8px',
  fontSize: 12, color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1', borderRadius: 4,
  outline: 'none', fontFamily: 'inherit',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
const textareaStyle = { ...inputStyle, fontSize: 11, lineHeight: 1.5 };
